import "zod/compile";
import { z } from "zod";
import {
  type FeedbackSupplier,
  type HubbellFeedbackDivision,
} from "./hubbell-feedback.contract";
import { exceptionFileScope } from "./exception-file-scope";
import { RENEWAL_RULE_MAX_ITEMS } from "./renewal-rules.contract";

/** A product-only list rejects formula text and multiword titles as item codes. */
const ProductHoldItemSchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._/+()-]*$/);
const ProductHoldItemsSchema = z
  .array(ProductHoldItemSchema)
  .min(1)
  .max(RENEWAL_RULE_MAX_ITEMS);

/** The established customer filename supplies the Division, never the selected Feedback Files. */
export function productHoldDivision(
  fileName: string,
  supplier: FeedbackSupplier,
): HubbellFeedbackDivision | undefined {
  const scope = exceptionFileScope(fileName);
  return !scope.ambiguous && scope.supplier === supplier
    ? scope.division
    : undefined;
}

/** First-column items only, with an optional AS400 ITEM header and blank rows. */
export function productHoldItems(
  rows: readonly (readonly string[])[],
): string[] | null {
  const populated = rows.filter((row) =>
    row.some((cell) => cell.trim() !== ""),
  );
  if (populated.some((row) => row.slice(1).some((cell) => cell.trim() !== "")))
    return null;
  const values = populated.map((row) => row[0]?.trim() ?? "");
  if (values[0]?.toUpperCase() === "AS400 ITEM") values.shift();
  const parsed = ProductHoldItemsSchema.safeParse(values);
  return parsed.success
    ? [...new Set(parsed.data.map((item) => item.toUpperCase()))]
    : null;
}
